import { AbsoluteFill, interpolate, useCurrentFrame } from "remotion";
import {
  Caption,
  Callout,
  CodePanel,
  COLORS,
  FONTS,
  MetricCounter,
  Panel,
  PipelineGraph,
  SceneTransition,
  SlideTimeBar,
  type PipeNode,
} from "../../coldcompute";
import { SCENE } from "./constants";
import {
  HOOK,
  RRF,
  RRF_CODE,
  CRAG,
  CRAG_FLOW,
  DIMS,
  PAYOFF,
  OUTRO,
} from "./script";

const center: React.CSSProperties = {
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
};

const Kicker: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 22,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color,
      marginBottom: 22,
    }}
  >
    ▍{text}
  </div>
);

const Big: React.FC<{ children: React.ReactNode; size?: number }> = ({
  children,
  size = 92,
}) => (
  <div
    style={{
      fontFamily: FONTS.display,
      fontWeight: 700,
      fontSize: size,
      lineHeight: 0.98,
      letterSpacing: "-0.02em",
      color: COLORS.text,
      textAlign: "center",
      maxWidth: 1560,
    }}
  >
    {children}
  </div>
);

/* 01 — HOOK : three letters + a number */
export const Scene01Hook: React.FC = () => {
  const frame = useCurrentFrame();
  const reveal = (at: number) =>
    interpolate(frame, [at, at + 14], [0, 1], {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.hook.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="the rag stack · 3 ideas" color={COLORS.cyan} />
          <div style={{ display: "flex", alignItems: "baseline", gap: 26 }}>
            <span style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 118, color: COLORS.cyan, opacity: reveal(18) }}>RRF</span>
            <span style={{ fontFamily: FONTS.display, fontSize: 64, color: COLORS.dim, opacity: reveal(34) }}>·</span>
            <span style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 118, color: COLORS.violet, opacity: reveal(40) }}>CRAG</span>
            <span style={{ fontFamily: FONTS.display, fontSize: 64, color: COLORS.dim, opacity: reveal(56) }}>·</span>
            <span style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 118, color: COLORS.lime, opacity: reveal(62) }}>1536</span>
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={HOOK} accent="cyan" />
    </AbsoluteFill>
  );
};

/* 02 — RRF : multiply explodes vs RRF bounded */
export const Scene02Rrf: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.rrf.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="reciprocal rank fusion" color={COLORS.cyan} />
        <div style={{ display: "flex", gap: 28, alignItems: "stretch" }}>
          <Panel accent="red" title="old · multiply ranks" width={500}>
            <div style={{ fontFamily: FONTS.mono, fontSize: 22, lineHeight: 1.9, color: COLORS.text }}>
              <div>0.35 × 1.5 × 2.5 × 3.0</div>
              <div style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 56, color: COLORS.red, marginTop: 6 }}>= 3.94</div>
              <div style={{ color: COLORS.dim, fontSize: 18 }}>one signal explodes the score</div>
            </div>
          </Panel>
          <Panel accent="cyan" title="rrf · add bounded bonus" width={500}>
            <div style={{ fontFamily: FONTS.mono, fontSize: 22, lineHeight: 1.9, color: COLORS.text }}>
              <div>Σ 1 / (60 + rank)</div>
              <div style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 56, color: COLORS.cyan, marginTop: 6 }}>≤ 0.016</div>
              <div style={{ color: COLORS.dim, fontSize: 18 }}>per signal · no runaway</div>
            </div>
          </Panel>
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={RRF} accent="cyan" />
  </AbsoluteFill>
);

/* 03 — RRF CODE : the fusion loop */
const RRF_SQL = `// rrf-scoring.ts — Reciprocal Rank Fusion (Cormack 2009)
const RRF_K = 60   // higher k = more weight to lower ranks

for (const chunk of chunks) {
  let score = 0
  // each signal contributes a BOUNDED bonus: max 1/(60+1)
  score += 1 / (RRF_K + hybridRank)     // vector + BM25
  score += 1 / (RRF_K + densityRank)    // query-term density
  score += 1 / (RRF_K + pageRankRank)   // import centrality
  score += 1 / (RRF_K + uiConceptRank)  // UI concept tags
  score += 1 / (RRF_K + chunkTypeRank)  // summary vs code
  rrfScores.set(chunk.id, score)        // up to 5 signals
}`;
export const Scene03RrfCode: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.rrfCode.duration} />
    <AbsoluteFill style={{ alignItems: "center", justifyContent: "center", paddingBottom: 40 }}>
      <CodePanel
        code={RRF_SQL}
        language="tsx"
        filename="src/lib/rag/rrf-scoring.ts"
        highlightLines={[6, 7]}
        revealFrames={220}
        fontSize={24}
        width={1260}
      />
    </AbsoluteFill>
    <Caption lines={RRF_CODE} accent="cyan" />
  </AbsoluteFill>
);

/* 04 — CRAG : the grader */
export const Scene04Crag: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.crag.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="corrective rag · the grader" color={COLORS.violet} />
        <Big size={70}>
          Retrieval isn't trusted.
          <br />
          <span style={{ color: COLORS.violet }}>It's graded.</span>
        </Big>
        <div style={{ fontFamily: FONTS.body, fontSize: 28, color: COLORS.dim, marginTop: 30, maxWidth: 1200, textAlign: "center" }}>
          A small fast LLM reads the chunks and asks: do these actually answer the question — or just
          <span style={{ color: COLORS.text }}> sound like </span> they do?
        </div>
        <div style={{ fontFamily: FONTS.mono, fontSize: 20, color: COLORS.violet, marginTop: 26, letterSpacing: "0.1em" }}>
          quality-gate.ts · CRAG paper, Yan et al. 2024
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={CRAG} accent="violet" />
  </AbsoluteFill>
);

/* 05 — CRAG FLOW : three verdicts → three actions */
const VERDICT_PIPE: PipeNode[] = [
  { label: "chunks", value: "GRADE", sub: "fast LLM", accent: "cyan" },
  { label: "correct", value: "PASS", sub: "use as-is", accent: "green" },
  { label: "ambiguous", value: "BROADEN", sub: "lower threshold", accent: "amber" },
  { label: "incorrect", value: "HyDE", sub: "hypothetical embed", accent: "magenta" },
];
export const Scene05CragFlow: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.cragFlow.duration} />
    <AbsoluteFill style={{ ...center, padding: "0 90px" }}>
      <Kicker text="3 verdicts → 3 corrections" color={COLORS.violet} />
      <div style={{ width: 1680 }}>
        <PipelineGraph nodes={VERDICT_PIPE} startFrame={6} stepFrames={24} nodeWidth={230} />
      </div>
    </AbsoluteFill>
    <Caption lines={CRAG_FLOW} accent="violet" />
  </AbsoluteFill>
);

/* 06 — DIMS : why 1536 */
export const Scene06Dims: React.FC = () => {
  const frame = useCurrentFrame();
  const tag = interpolate(frame, [80, 110], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.dims.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="embedding width" color={COLORS.lime} />
        <MetricCounter value={1536} from={0} startFrame={8} durationFrames={55} accent="lime" fontSize={172} format={(n) => Math.round(n).toLocaleString()} />
        <div style={{ fontFamily: FONTS.mono, fontSize: 24, color: COLORS.dim, marginTop: 6, letterSpacing: "0.06em" }}>
          dimensions per vector · pgvector column
        </div>
        <div style={{ display: "flex", gap: 30, marginTop: 38, opacity: tag }}>
          <Callout label="qwen3-embedding-8b" accent="lime" width={360}>
            #1 on <b style={{ color: COLORS.text }}>MTEB-Code</b> · 32K context
          </Callout>
          <Callout label="matryoshka truncation" accent="green" width={360}>
            native 4096 → kept <b style={{ color: COLORS.text }}>1536</b> for pgvector compat
          </Callout>
        </div>
      </AbsoluteFill>
      <Caption lines={DIMS} accent="lime" />
    </AbsoluteFill>
  );
};

/* 07 — PAYOFF : the three layers fused */
const STACK: PipeNode[] = [
  { label: "embed", value: "1536-D", sub: "qwen3", accent: "lime" },
  { label: "fuse", value: "RRF ×5", sub: "bounded", accent: "cyan" },
  { label: "verify", value: "CRAG", sub: "grade + correct", accent: "violet" },
  { label: "answer", value: "GROUNDED", sub: "cited", accent: "green" },
];
export const Scene07Payoff: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.payoff.duration} />
    <AbsoluteFill style={{ ...center, padding: "0 80px" }}>
      <Kicker text="embed · fuse · verify" color={COLORS.green} />
      <div style={{ width: 1700 }}>
        <PipelineGraph nodes={STACK} startFrame={6} stepFrames={22} nodeWidth={230} />
      </div>
    </AbsoluteFill>
    <Caption lines={PAYOFF} accent="green" />
  </AbsoluteFill>
);

/* 08 — OUTRO */
export const Scene08Outro: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.outro.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Big size={64}>
          Fusion you can bound.
          <br />
          A grader you can trust.
          <br />
          <span style={{ color: COLORS.cyan }}>Dimensions you chose.</span>
        </Big>
        <div style={{ fontFamily: FONTS.mono, fontSize: 24, letterSpacing: "0.24em", color: COLORS.dim, marginTop: 44 }}>
          EAKC · grounded retrieval
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={OUTRO} accent="cyan" />
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
